import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Save, RefreshCw, AlertTriangle, X, CheckCircle, UserCheck } from 'lucide-react';
import MainService from '../../services/MainService';

const AddAssignment = () => {
  const { vehicleId, assignmentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [assignment, setAssignment] = useState({
    Id: 0,
    VehicleId: vehicleId || '',
    DriverId: '',
    EmployeeId: '',
    AssignedDate: '',
    UnassignedDate: '',
    Notes: ''
  });
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    loadVehicles();
    loadDrivers();
    loadEmployees();
    if (assignmentId) {
      loadAssignment();
    }
  }, [assignmentId]);

  useEffect(() => {
    if (!assignmentId && vehicleId) {
      setAssignment((prev) => ({ ...prev, VehicleId: vehicleId }));
    }
  }, [vehicleId, assignmentId]);

  const loadVehicles = async () => {
    try {
      const response = await MainService.VehiclesInDropDown();
      setVehicles(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error loading vehicles:', err);
      setVehicles([]);
    }
  };

  const loadDrivers = async () => {
    try {
      const response = await MainService.DriversInDropDown();
      setDrivers(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error loading drivers:', err);
      setDrivers([]);
    }
  };

  const loadEmployees = async () => {
    try {
      const response = await MainService.EmployeesInDropDown();
      setEmployees(Array.isArray(response) ? response : []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setEmployees([]);
    }
  };

  const loadAssignment = async () => {
    try {
      setLoading(true);
      const response = await MainService.VehicleAssignmentById(assignmentId);
      if (response) {
        setAssignment((prev) => ({
          ...prev,
          Id: response.Id ?? response.ID ?? 0,
          VehicleId: String(response.VehicleId ?? response.VehicleID ?? vehicleId ?? ''),
          DriverId: String(response.DriverId ?? response.DriverID ?? ''),
          EmployeeId: String(response.EmployeeId ?? response.EmployeeID ?? ''),
          AssignedDate: response.AssignedDate || '',
          UnassignedDate: response.UnassignedDate || '',
          Notes: response.Notes || ''
        }));
      }
    } catch (err) {
      console.error('Error loading assignment:', err);
      setError('Failed to load assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setAssignment(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!assignment.VehicleId) {
      setError('Please select vehicle');
      return;
    }
    if (!assignment.DriverId && !assignment.EmployeeId) {
      setError('Select a driver or an employee driver');
      return;
    }
    if (!assignment.AssignedDate) {
      setError('Please select assigned date');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const payload = {
        Id: Number(assignment.Id) || 0,
        VehicleId: Number(assignment.VehicleId),
        DriverId: assignment.DriverId ? Number(assignment.DriverId) : null,
        EmployeeId: assignment.EmployeeId ? Number(assignment.EmployeeId) : null,
        AssignedDate: assignment.AssignedDate,
        UnassignedDate: assignment.UnassignedDate || null,
        Notes: assignment.Notes || ''
      };
      const response = await MainService.SaveVehicleAssignment(payload);
      const ok =
        response === true ||
        response === 'true' ||
        response === 'Success' ||
        (response &&
          typeof response === 'object' &&
          (response.Id != null || response.ID != null));
      if (!ok) {
        setError('Failed to save assignment');
        return;
      }
      setSuccess('Assignment saved successfully');
      setTimeout(() => {
        const vid = assignment.VehicleId || vehicleId;
        navigate(
          vid
            ? `/home/vehicle-management/assignments/${vid}`
            : '/home/vehicle-management/assignments'
        );
      }, 800);
    } catch (err) {
      console.error('Error saving assignment:', err);
      setError('Failed to save assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setAssignment({
      Id: 0,
      VehicleId: vehicleId || '',
      DriverId: '',
      EmployeeId: '',
      AssignedDate: '',
      UnassignedDate: '',
      Notes: ''
    });
    setError('');
    setSuccess('');
  };

  return (
    <div className="space-y-6">
      {/* Toast Messages */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div className="fixed top-4 right-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg shadow-lg flex items-center gap-3 z-50 min-w-[300px]">
          <CheckCircle className="w-5 h-5 flex-shrink-0" />
          <span className="flex-1">{success}</span>
          <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700 flex-shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <UserCheck className="w-6 h-6 text-primary mr-2" />
            <h1 className="text-3xl font-bold text-gray-900">
              {assignmentId ? 'Edit Assignment' : 'Add Assignment'}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleReset}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <RefreshCw className="w-4 h-4" />
              <span>Reset</span>
            </button>
            <button
              onClick={handleSave}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-[#46955A] text-white rounded-lg hover:bg-[#3d7d4f] transition-colors disabled:opacity-50"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              <span>Save</span>
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-500 ml-8">
          {assignmentId ? 'Update driver assignment' : 'Assign a driver to a vehicle'}
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Vehicle */}
          {!vehicleId ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vehicle <span className="text-red-500">*</span>
              </label>
              <select
                name="VehicleId"
                value={assignment.VehicleId}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
              >
                <option value="">Select Vehicle</option>
                {vehicles.map(vehicle => (
                  <option key={vehicle.Id} value={vehicle.Id}>
                    {vehicle.PlateNumber || vehicle.Number || vehicle.Name}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle</label>
              <input
                type="text"
                value={vehicleId}
                disabled
                className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 bg-gray-50 cursor-not-allowed"
              />
            </div>
          )}

          {/* Driver (fleet) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Driver (fleet)
            </label>
            <select
              name="DriverId"
              value={assignment.DriverId}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            >
              <option value="">Select driver</option>
              {drivers.map(driver => (
                <option key={driver.Id} value={driver.Id}>
                  {driver.Name || driver.EmployeeName}
                </option>
              ))}
            </select>
          </div>

          {/* Employee driver */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Employee driver
            </label>
            <select
              name="EmployeeId"
              value={assignment.EmployeeId}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            >
              <option value="">Select employee</option>
              {employees.map((emp) => (
                <option key={emp.Id} value={emp.Id}>
                  {emp.Name || emp.EmployeeName}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Choose at least one: fleet driver or employee.</p>
          </div>

          {/* Assigned Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assigned Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="AssignedDate"
              value={assignment.AssignedDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!assignment.AssignedDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Unassigned Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Unassigned Date</label>
            <input
              type="date"
              name="UnassignedDate"
              value={assignment.UnassignedDate}
              onChange={handleInputChange}
              className={`w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent ${!assignment.UnassignedDate ? 'text-gray-400' : 'text-gray-900'}`}
            />
          </div>

          {/* Notes */}
          <div className="md:col-span-2 lg:col-span-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="Notes"
              value={assignment.Notes}
              onChange={handleInputChange}
              placeholder="Enter additional notes"
              rows="3"
              className="w-full px-4 py-2 border-0 border-b-2 border-gray-300 focus:outline-none focus:border-[#46955A] bg-transparent"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAssignment;

